import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import error from '../../../../common/nodejs/errors';

const PHASE = {
  STANDBY: 'standby',
  MONITOR: 'monitor',
  SUCCESS: 'success',
  ERROR: 'error',
  COMMON_ERROR: 'commonError',
};

const API_TYPE = {
  EXECUTE: 'execute',
  MONITOR: 'monitor',
  DISPOSE: 'dispose',
  TERMINATE: 'terminate',
};

// REQ.WS.177.SBY.3で定義されている入力制限
const SSID_MIN_LENGTH = 1;
const SSID_MAX_LENGTH = 32;
const PASS_MIN_LENGTH = 8;
const PASS_MAX_LENGTH = 63;

export default {
  name: 'requirement177',

  data() {
    return {
      phase: PHASE.STANDBY,
      isLoading: false,
      isDispose: false,
      systemId: '',
      modal_requirement_177: false,
      inputSsid: '',
      inputPass: '',
      ssidValidationMessage: '',
      passValidationMessage: '',
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /**
     * ダイアログのヘッダーカラーをフェーズで切り替える
     * @returns {string} ヘッダーカラー
     */
    headerColor() {
      const colorMap = {
        [PHASE.SUCCESS]: 'success',
        [PHASE.ERROR]: 'danger',
        [PHASE.COMMON_ERROR]: 'danger',
      };
      // 停止・エラー以外はsecondaryを返す。
      return colorMap[this.phase] || 'secondary';
    },

    /**
     * ダイアログのヘッダータイトルをフェーズで切り替える
     * @returns {string} ヘッダータイトル
     */
    headerTitle() {
      const titleMap = {
        [PHASE.STANDBY]: 'message.common_title_confirmation',
        [PHASE.MONITOR]: 'individual.common_title_execution',
        [PHASE.SUCCESS]: 'message.common_title_success',
        [PHASE.ERROR]: 'message.common_title_error',
        [PHASE.COMMON_ERROR]: 'message.common_title_error',
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * フェーズに応じてダイアログに表示する文言を切り替える
     * @returns {string} ダイアログに表示する文言
     */
    dialogMessage() {
      const messageMap = {
        [PHASE.STANDBY]: `${this.i18nWsKey}.REQ_WS_177_TXT_1`,
        [PHASE.MONITOR]: `${this.i18nWsKey}.REQ_WS_177_TXT_2_0001`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_177_TXT_3_0002`,
        [PHASE.ERROR]: `${this.i18nWsKey}.REQ_WS_177_TXT_4_0010`,
        [PHASE.COMMON_ERROR]: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase]);
    },

    /**
     * 入力フォームの表示・非表示を制御する
     * @returns {boolean} 入力フォームを表示するかどうか
     */
    showInputForm() {
      // 待機画面だけ入力フォームを表示する
      return this.phase === PHASE.STANDBY;
    },

    /**
     * キャンセルボタンの表示・非表示を制御する
     * @returns {boolean} キャンセルボタンを表示するかどうか
     */
    showButtonCancel() {
      // 待機画面だけキャンセルボタンを表示する
      return this.phase === PHASE.STANDBY;
    },

    /**
     * OKボタンの表示・非表示を制御する
     * @returns {boolean} OKボタンを表示するかどうか
     */
    showButtonOk() {
      // 実行監視画面はOKボタンを表示しない
      return this.phase !== PHASE.MONITOR;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /**
     * コンポーネントがマウントされた際に呼び出されるライフサイクルフック
     */
    init() {
      this.phase = PHASE.STANDBY;
      this.isLoading = false;
      this.isDispose = false;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_177 = true;
      this.inputSsid = '';
      this.inputPass = '';
      this.clearValidationResult();
    },

    /**
     * バリデーション結果をクリアする
     */
    clearValidationResult() {
      this.ssidValidationMessage = '';
      this.passValidationMessage = '';
    },

    /**
     * 入力値のバリデーションチェックを行う
     * @param {string} value 入力値
     * @param {number} minLength 最小文字数
     * @param {number} maxLength 最大文字数
     * @returns {string} バリデーション結果のメッセージ（エラーがなければ空文字）
     */
    validateValue(value, minLength, maxLength) {
      if (!/^[\x20-\x7E]+$/.test(value)) {
        return this.$t('message.alert_validate_invalid_pattern');
      }
      if (value.length < minLength || value.length > maxLength) {
        return this.$t('message.alert_validate_max_length', [`${minLength}~${maxLength}`]);
      }
      return '';
    },

    /**
     * 入力値のバリデーションチェックとエラーメッセージを設定する
     * @returns {boolean} いずれの入力値も問題無ければtrue、入力エラーが発生していればfalseを返す。
     */
    validateInputs() {
      this.ssidValidationMessage = this.validateValue(this.inputSsid, SSID_MIN_LENGTH, SSID_MAX_LENGTH);
      this.passValidationMessage = this.validateValue(this.inputPass, PASS_MIN_LENGTH, PASS_MAX_LENGTH);
      return !this.ssidValidationMessage && !this.passValidationMessage;
    },

    /**
     * フェーズに応じたOKボタン押下時の処理を実行する
     */
    async onOkClicked() {
      if (this.phase === PHASE.STANDBY) {
        const isValid = this.validateInputs();
        if (isValid) {
          // 待機画面で入力値に問題無ければ、実行フェーズに移る
          await this.execute();
        }
        return;
      }

      // 停止やエラー画面でOKボタンが押下されたら、何も処理を行わずに機能終了する。
      this.closeWizard();
    },

    /**
     * キャンセルボタンが押下されたときの処理
     */
    onCancelClicked() {
      // 待機画面でキャンセルボタンが押下されたら、CCとの通信は発生していないのでそのまま機能終了する。
      this.closeWizard();
    },

    /**
     * セッション遷移からルーチン実行までの処理開始を要求する
     */
    async execute() {
      this.isLoading = true;
      try {
        const params = { ssid: this.inputSsid, pass: this.inputPass };
        const response = await this.polling(
          API_TYPE.EXECUTE,
          params,
          // executeでis_disposeの状態が不明な場合は、disposeやterminateを呼ばずにエラーを表示する。
          () => this.showUnexpectedError(),
        );

        if (!response) {
          // responseがnullになるケースはpollingの中でエラー処理を実行済みなので、executeの処理は中断する。
          return;
        }

        if (typeof response.data?.user_data?.is_dispose !== 'boolean') {
          // FE/BE間の契約が守られていれば発生しない。
          this.showUnexpectedError();
          return;
        }

        this.isDispose = response.data.user_data.is_dispose;
        if (response.data?.status === REQUEST_STATUS_FAILED) {
          await this.completeWithExit(PHASE.COMMON_ERROR);
          return;
        }

        // この時点でルーチン実行まで正常終了しているので、実行監視に移る。
        await this.monitor();
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 実行監視を実行し、処理結果によって画面遷移を行う。
     */
    async monitor() {
      this.phase = PHASE.MONITOR;

      /** @type {Promise<void> | undefined} */
      let exitProcess;
      const response = await this.polling(
        API_TYPE.MONITOR,
        {},
        () => {
          // 予期せぬエラーが発生したら、isDisposeの状態でdispose/terminateを実行した後に共通エラーを表示する
          exitProcess = this.completeWithExit(PHASE.COMMON_ERROR);
        },
      );
      if (!response) {
        if (exitProcess) {
          // exitProcessが存在する場合は、完了処理を待機する。(API通信が完了するのを待つ)
          // ここで待機しないと内部的に、通信が完了する前にisLoadingがfalseになってしまう。
          await exitProcess;
        }
        return;
      }

      const nextPhase = (() => {
        if (response.data?.status === REQUEST_STATUS_COMPLETED) {
          return PHASE.SUCCESS;
        }
        if (response.data?.async_code === error.WSP_177_CONNECTION_ERROR) {
          return PHASE.ERROR;
        }
        return PHASE.COMMON_ERROR;
      })();
      await this.completeWithExit(nextPhase);
    },

    /**
     * 共通エラーを表示する。
     */
    showUnexpectedError() {
      this.phase = PHASE.COMMON_ERROR;
    },

    /**
     * isDisposeに応じてAPIを実行後に、指定された画面へ遷移する。
     * @param {string} phase 遷移先の画面フェーズ
     */
    async completeWithExit(phase) {
      const apiType = this.isDispose ? API_TYPE.DISPOSE : API_TYPE.TERMINATE;
      await this.polling(apiType, {});

      // 終了APIの応答内容にかかわらず、事前に指定した画面に遷移する。
      this.phase = phase;
    },

    /**
     * 指定されたシーケンスのAPIをポーリングする。
     * @param {string} type execute or monitor
     * @param {object} parameters APIに渡すパラメータ
     * @param {function} onUnexpectedError 予期せぬエラー発生時のコールバック
     * @returns {Promise<object>} APIのレスポンス
     */
    async polling(type, parameters = {}, onUnexpectedError = () => { }) {
      const token = session.getToken();

      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        { id_token: token.id_token, type, ...parameters },
        onUnexpectedError,
        result => this.validateErrorResult(result, onUnexpectedError),
        result => this.validateErrorResult(result, onUnexpectedError),
      );

      return response;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {function} onUnexpectedError 予期せぬエラー発生時のコールバック
     */
    validateErrorResult(result, onUnexpectedError) {
      apiErrorDefaultAction(
        this,
        result,
        () => { this.logout(this); },
        () => { this.resetSession(this); },
        () => {
          onUnexpectedError();
        },
      );
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.inputSsid = '';
      this.inputPass = '';
      this.isDispose = false;
      this.clearValidationResult();
      this.modal_requirement_177 = false;
      this.$emit('close-wizard');
    },
  },

  /**
   * コンポーネントがマウントされた際に呼び出されるライフサイクルフック
   */
  mounted() {
    this.init();
  },
};